import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Hook que verifica se o usuário atual participa de algum plano estratégico.
 * 
 * Participação inclui:
 * - Ser membro de um plano (strategic_plan_members)
 * - Ser responsável por um plano (strategic_plans.responsible_id)
 * - Ter solicitação de acesso aprovada (onepage_view_access_requests)
 * 
 * Usado para determinar se usuário PADRAO deve ter acesso ao módulo estratégico.
 */
export const useStrategicParticipation = () => {
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ["strategic-participation", user?.id],
    queryFn: async () => {
      if (!user?.id) return { hasParticipation: false };

      // Verificar se é membro de algum plano
      const { count: memberCount, error: memberError } = await supabase
        .from("strategic_plan_members")
        .select("id", { count: "exact", head: true })
        .eq("user_id", user.id);

      if (memberError) {
        console.error("Error checking strategic plan membership:", memberError);
      }

      if (memberCount && memberCount > 0) {
        return { hasParticipation: true };
      }

      // Verificar se é responsável por algum plano
      const { count: responsibleCount, error: responsibleError } = await supabase
        .from("strategic_plans")
        .select("id", { count: "exact", head: true })
        .eq("responsible_id", user.id)
        .is("deleted_at", null);

      if (responsibleError) {
        console.error("Error checking strategic plan responsibility:", responsibleError);
      }

      if (responsibleCount && responsibleCount > 0) {
        return { hasParticipation: true };
      }

      // Verificar se tem acesso view-only aprovado
      const { count: accessCount, error: accessError } = await supabase
        .from("onepage_access_requests")
        .select("id", { count: "exact", head: true })
        .eq("requester_id", user.id)
        .eq("status", "APPROVED");

      if (accessError) {
        console.error("Error checking view-only access:", accessError);
      }

      if (accessCount && accessCount > 0) {
        return { hasParticipation: true };
      }

      return { hasParticipation: false };
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // Cache por 5 minutos
    gcTime: 10 * 60 * 1000,
  });

  return {
    hasStrategicParticipation: data?.hasParticipation ?? false,
    isLoading,
  };
};
